import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Upload, Download, Edit2, Trash2, Shield, Search, AlertTriangle, CheckCircle, Clock, FileText, RefreshCw, ScanLine, Mail, X, Check, HardHat, Eye, QrCode, Users, FolderPen } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { CardCropAdjuster } from './CardCropAdjuster';
import HRDashboardCompact from './HRDashboardCompact';
import MassTrainingPanel from './MassTrainingPanel';
import TicketRenameTool from './TicketRenameTool';
import { generateEmployeeTicketsPdf } from '../utils/ticketsPdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CERT_VIEW_ROLES = ['super_admin', 'admin', 'office_admin', 'hr', 'fabrication_manager', 'dispatch', 'shop_foreman', 'millwright_foreman', 'foreman', 'project_manager', 'project_manager_estimator', 'estimator'];

const SafetyTickets = () => {
  const { user } = useAuth();
  const canSeeEmployees = CERT_VIEW_ROLES.includes(user?.role);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [activeTab, setActiveTab] = useState(canSeeEmployees ? 'employees' : 'tickets');
  const [bundleEmployees, setBundleEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [bundleEmail, setBundleEmail] = useState('');
  const [bundleSending, setBundleSending] = useState(false);
  const [pdfEmployeeId, setPdfEmployeeId] = useState('');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfProgress, setPdfProgress] = useState('');
  const [bulkStatus, setBulkStatus] = useState(null);
  const bulkTimer = useRef(null);
  const bulkWasRunning = useRef(false);
  const bulkPollFails = useRef(0);

  const pollBulkStatus = async () => {
    try {
      const r = await axios.get(`${API}/safety-cards/employee-tickets-pdf/bulk-status`);
      bulkPollFails.current = 0;
      setBulkStatus(r.data);
      if (r.data.status === 'running' || r.data.status === 'starting') {
        bulkWasRunning.current = true;
        bulkTimer.current = setTimeout(pollBulkStatus, 4000);
      } else if (bulkWasRunning.current) {
        bulkWasRunning.current = false;
        if (r.data.status === 'done') {
          const ok = (r.data.results || []).filter(x => x.status === 'done').length;
          toast.success(`Bulk PDFs complete — ${ok}/${r.data.total} employees. Old PDFs moved to each person's "Expired" folder in Drive.`, { duration: 10000 });
        } else if (r.data.status === 'interrupted') {
          toast.warning(r.data.message || 'Bulk generation was interrupted — hit Resume to finish the rest.', { duration: 12000 });
        } else if (r.data.status === 'error') {
          toast.error(r.data.error || 'Bulk generation failed');
        }
      }
    } catch (e) {
      // A blip (server busy / restarting) must NOT freeze the progress display — keep polling
      console.error('bulk status poll failed:', e);
      bulkPollFails.current += 1;
      if (bulkWasRunning.current && bulkPollFails.current <= 40) {
        bulkTimer.current = setTimeout(pollBulkStatus, 8000);
      }
    }
  };

  useEffect(() => {
    if (hasPermission) pollBulkStatus();
    if (hasPermission) {
      axios.get(`${API}/safety/rescan-status`).then((r) => {
        setDriveScanStatus(r.data);
        if (r.data.status === 'running') { setDriveScanning(true); setTimeout(pollDriveScanStatus, 3000); }
      }).catch(() => {});
    }
    return () => { if (bulkTimer.current) clearTimeout(bulkTimer.current); };
  }, []);

  const handleBulkGenerate = async (resume = false) => {
    const confirmMsg = resume
      ? 'Resume the bulk PDF run?\n\nEmployees whose PDF was already finished are skipped — only the remaining ones are generated.'
      : 'Generate a fresh Safety Tickets PDF for EVERY employee?\n\nEach person\'s old PDF is moved to an "Expired" folder inside their Drive folder, then a fresh one is saved. This runs on the server and can take a while.';
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.post(`${API}/safety-cards/employee-tickets-pdf/bulk-start`, resume ? { resume: true } : {});
      setBulkStatus({ status: 'starting', processed: 0, total: 0 });
      bulkWasRunning.current = true;
      bulkPollFails.current = 0;
      bulkTimer.current = setTimeout(pollBulkStatus, 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start bulk generation');
    }
  };

  const bulkRunning = bulkStatus && ['starting', 'running'].includes(bulkStatus.status);

  const [qrGenerating, setQrGenerating] = useState(false);
  const handleQrStickers = async () => {
    setQrGenerating(true);
    try {
      const r = await axios.post(`${API}/qr-stickers`, { base_url: window.location.origin }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Hardhat QR Stickers.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('Hardhat QR sticker sheet downloaded — scanning a sticker shows that person\'s live tickets, no login needed.');
    } catch (e) {
      toast.error('Could not generate the QR stickers');
    } finally {
      setQrGenerating(false);
    }
  };

  const handleGenerateTicketsPdf = async () => {
    if (!pdfEmployeeId) return;
    const emp = allEmployees.find(e => e.id === pdfEmployeeId);
    setPdfGenerating(true);
    setPdfProgress('Starting…');
    try {
      const res = await generateEmployeeTicketsPdf({ employee_id: pdfEmployeeId }, (m) => setPdfProgress(m));
      toast.success(
        `${emp?.full_name || 'Employee'}: ${res.included} ticket(s) in the PDF` +
        (res.from_cache ? ' — no new tickets, reused the last generated PDF' : '') +
        `${res.excluded_expired ? `, ${res.excluded_expired} expired skipped` : ''}. Saved to their Drive folder.`,
        { duration: 9000 }
      );
      if (res.download_url) window.open(res.download_url, '_blank');
    } catch (e) {
      toast.error(e.message || 'Failed to generate the Safety Tickets PDF');
    } finally {
      setPdfGenerating(false);
      setPdfProgress('');
    }
  };
  const [driveScanning, setDriveScanning] = useState(false);
  const [driveScanStatus, setDriveScanStatus] = useState(null);
  const [cardReviews, setCardReviews] = useState([]);
  const [cardResults, setCardResults] = useState({});
  const [cardPending, setCardPending] = useState(0);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardPolling, setCardPolling] = useState(false);
  const [cardEdits, setCardEdits] = useState({});
  const [confirmingId, setConfirmingId] = useState(null);
  const [archiveModal, setArchiveModal] = useState(null); // { reviewId, candidates }
  const [archiveChecks, setArchiveChecks] = useState({}); // file_id -> bool
  const [archiving, setArchiving] = useState(false);

  const [formData, setFormData] = useState({
    employee_name: '',
    employee_email: '',
    certificate_type: '',
    course_name: '',
    issuing_organization: '',
    certificate_number: '',
    issue_date: '',
    expiry_date: '',
    equipment_type: '',
    classes_covered: '',
    trainer_name: '',
    reference_number: '',
    description: '',
    project_id: ''
  });

  // Check if user has permission to manage safety tickets
  const hasPermission = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'office_admin' || user?.role === 'project_manager' || user?.role === 'project_manager_estimator';

  // Subcontractor certs are managed by a broader set of roles (matches employee cert managers)
  const SUB_CERT_ROLES = ['super_admin', 'admin', 'office_admin', 'hr', 'project_manager', 'project_manager_estimator', 'estimator', 'foreman', 'shop_foreman', 'millwright_foreman', 'fabrication_manager'];
  const canManageSubs = SUB_CERT_ROLES.includes(user?.role);

  const [subCerts, setSubCerts] = useState([]);
  const [isSubOpen, setIsSubOpen] = useState(false);
  const [isSubEditOpen, setIsSubEditOpen] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [subUploading, setSubUploading] = useState(false);
  const [subFile, setSubFile] = useState(null);
  const [subForm, setSubForm] = useState({
    company_name: '', worker_name: '', certificate_type: '',
    issue_date: '', expiry_date: '', work_order_number: '', notes: ''
  });

  const loadEmployees = async () => {
    try {
      const r = await axios.get(`${API}/users`);
      const users = Array.isArray(r.data) ? r.data : r.data.users || [];
      setAllEmployees(users.filter(u => u.is_active !== false && u.full_name));
    } catch (e) {
      toast.error('Could not load the employee list — tap "reload list" or refresh to retry.');
    }
  };

  useEffect(() => {
    fetchTickets();
    loadEmployees();
  }, []);

  // Load subcontractor certs + default the tab for roles that only manage subs
  useEffect(() => {
    if (canManageSubs) fetchSubCerts();
    if (!canSeeEmployees) setActiveTab(hasPermission ? 'tickets' : 'subcontractors');
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/safety-tickets`);
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching safety tickets:', error);
      toast.error('Failed to load safety tickets');
    } finally {
      setLoading(false);
    }
  };

  const pollDriveScanStatus = async () => {
    try {
      const r = await axios.get(`${API}/safety/rescan-status`);
      setDriveScanStatus(r.data);
      if (r.data.status === 'running') {
        setTimeout(pollDriveScanStatus, 3000);
      } else {
        setDriveScanning(false);
        if (r.data.status === 'completed') {
          const imported = r.data.tickets_imported || 0;
          const updated = r.data.tickets_updated || 0;
          toast.success(`Drive scan complete — ${imported} new, ${updated} updated across ${r.data.processed_employees || 0} employees`);
          fetchTickets();
        } else if (r.data.status === 'error') {
          toast.error(`Drive scan failed: ${r.data.error || 'Unknown error'}`);
        }
      }
    } catch (error) {
      setDriveScanning(false);
      console.error('Error polling drive scan status:', error);
    }
  };

  const handleRescanDrive = async () => {
    if (driveScanning) return;
    setDriveScanning(true);
    setDriveScanStatus({ status: 'running' });
    try {
      await axios.post(`${API}/safety/rescan-drive`);
      toast.info('Scanning Google Drive for new safety tickets…');
      setTimeout(pollDriveScanStatus, 2000);
    } catch (error) {
      setDriveScanning(false);
      if (error.response?.status === 409) {
        toast.info('A Drive scan is already running — checking status…');
        pollDriveScanStatus();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to start Drive scan');
      }
    }
  };

  const fetchCardReviews = async (silent = false) => {
    if (!silent) setCardLoading(true);
    try {
      const r = await axios.get(`${API}/safety-cards/reviews?status=open`);
      const reviews = r.data.reviews || [];
      setCardReviews(reviews);
      setCardPending(r.data.pending || 0);
      // Map per-card file results (from any prior confirm) so we can show filed/failed badges
      const resultsMap = {};
      reviews.forEach(rev => (rev.results || []).forEach(res => { if (res.card_id) resultsMap[res.card_id] = res; }));
      setCardResults(resultsMap);
      // Seed editable state, but NEVER overwrite an edit the user is in the middle of making
      // (a background refresh was reverting hand-typed dates / crop drags). Only add entries
      // for cards we don't already have.
      setCardEdits(prev => {
        const next = { ...prev };
        reviews.forEach(rev => {
          // Cards on one page are almost always from the same training day: if a card has
          // no detected expiry, default it to a sibling card's detected date.
          const siblingDate = (rev.cards || []).map(c => c.expiry_date).find(d => !!d) || '';
          (rev.cards || []).forEach(c => {
            if (next[c.card_id]) return; // preserve in-progress edit
            const res = resultsMap[c.card_id];
            next[c.card_id] = {
              include: res?.status !== 'filed',
              employee_id: c.matched_user_id || '',
              employee_name: c.matched_user_name || c.employee_name_detected || '',
              ticket_name: c.ticket_name || '',
              expiry_date: (c.expiry_date || siblingDate || '').slice(0, 10),
              crop_rect: c.crop_rect || null,
            };
          });
        });
        return next;
      });
    } catch (e) {
      toast.error('Failed to load card scans');
    } finally {
      if (!silent) setCardLoading(false);
    }
  };

  const handleCardPollNow = async () => {
    setCardPolling(true);
    try {
      const r = await axios.post(`${API}/safety-cards/poll-now`);
      if (r.data.status === 'disabled') {
        toast.info('Scan mailbox not configured yet (needs the inbox app password in production).');
        setCardPolling(false);
        return;
      }
      toast.info('Checking the inbox… new scans will appear here in a moment.');
      // Processing runs in the background (so multi-page scans can't time out).
      // Refresh the queue a few times over ~40s to surface new reviews as they land.
      let tries = 0;
      const iv = setInterval(async () => {
        tries += 1;
        await fetchCardReviews(true);
        if (tries >= 7) { clearInterval(iv); setCardPolling(false); }
      }, 6000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to check inbox');
      setCardPolling(false);
    }
  };

  const updateCardEdit = (cardId, field, value) => {
    setCardEdits(prev => ({ ...prev, [cardId]: { ...prev[cardId], [field]: value } }));
  };

  const selectCardEmployee = (cardId, empId) => {
    const emp = allEmployees.find(e => e.id === empId);
    setCardEdits(prev => ({
      ...prev,
      [cardId]: { ...prev[cardId], employee_id: empId, employee_name: emp?.full_name || '' },
    }));
  };

  const handleConfirmReview = async (review) => {
    const cards = (review.cards || []).map(c => ({
      card_id: c.card_id,
      include: cardEdits[c.card_id]?.include ?? true,
      employee_id: cardEdits[c.card_id]?.employee_id || null,
      employee_name: cardEdits[c.card_id]?.employee_name || '',
      ticket_name: cardEdits[c.card_id]?.ticket_name || '',
      expiry_date: cardEdits[c.card_id]?.expiry_date || null,
      crop_rect: cardEdits[c.card_id]?.crop_rect || null,
    }));
    const included = cards.filter(c => c.include);
    if (included.some(c => !c.employee_name)) {
      toast.error('Assign an employee to every included card first');
      return;
    }
    setConfirmingId(review.id);
    try {
      const r = await axios.post(`${API}/safety-cards/reviews/${review.id}/confirm`, { cards });
      if (r.data.errors > 0) {
        const errs = (r.data.results || []).filter(x => x.status === 'error');
        const reasons = [...new Set(errs.map(x => x.detail).filter(Boolean))].slice(0, 3).join('; ');
        toast.warning(`Filed ${r.data.filed} card(s). ${r.data.errors} still need attention${reasons ? `: ${reasons}` : ' (see the red note on each card)'}. Fix those and click Confirm & File All again — the ones already filed won't be duplicated.`, { duration: 9000 });
      } else {
        toast.success(`Filed ${r.data.filed} card(s) to Google Drive & safety records`);
      }
      fetchCardReviews();
      fetchTickets();
      // After filing, look for older copies of the same tickets to archive to "Expired Tickets"
      if (r.data.filed > 0) {
        checkExpiredCandidates(review.id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to file cards');
    } finally {
      setConfirmingId(null);
    }
  };

  const checkExpiredCandidates = async (reviewId) => {
    try {
      const r = await axios.post(`${API}/safety-cards/reviews/${reviewId}/expired-candidates`, {});
      const candidates = r.data?.candidates || [];
      if (candidates.length > 0) {
        const checks = {};
        candidates.forEach(cd => (cd.matches || []).forEach(m => { checks[m.file_id] = true; }));
        setArchiveChecks(checks);
        setArchiveModal({ reviewId, candidates });
      }
    } catch (e) {
      // Non-blocking: filing already succeeded. Just log.
      console.warn('expired-candidates check failed', e);
    }
  };

  const handleArchiveExpired = async () => {
    if (!archiveModal) return;
    const moves = [];
    (archiveModal.candidates || []).forEach(cd => {
      (cd.matches || []).forEach(m => {
        if (archiveChecks[m.file_id]) {
          moves.push({ file_id: m.file_id, tickets_folder_id: cd.tickets_folder_id, name: m.name });
        }
      });
    });
    if (moves.length === 0) {
      setArchiveModal(null);
      return;
    }
    setArchiving(true);
    try {
      const r = await axios.post(`${API}/safety-cards/reviews/${archiveModal.reviewId}/archive-expired`, { moves });
      const moved = r.data?.moved || 0;
      const errs = (r.data?.errors || []).length;
      if (errs > 0) {
        toast.warning(`Moved ${moved} old ticket(s) to Expired Tickets. ${errs} could not be moved.`, { duration: 8000 });
      } else {
        toast.success(`Moved ${moved} old ticket(s) to the Expired Tickets folder`);
      }
      setArchiveModal(null);
      setArchiveChecks({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to move tickets');
    } finally {
      setArchiving(false);
    }
  };

  const handleRejectReview = async (reviewId) => {
    if (!window.confirm('Discard this scan? The cards will not be filed.')) return;
    try {
      await axios.post(`${API}/safety-cards/reviews/${reviewId}/reject`);
      toast.success('Scan discarded');
      fetchCardReviews();
    } catch (e) {
      toast.error('Failed to discard scan');
    }
  };


  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        issue_date: new Date(formData.issue_date).toISOString(),
        expiry_date: formData.expiry_date ? new Date(formData.expiry_date).toISOString() : null
      };

      await axios.post(`${API}/safety-tickets`, submitData);
      toast.success('Safety ticket created successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchTickets();
    } catch (error) {
      console.error('Error creating safety ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to create safety ticket');
    }
  };

  const handleEditTicket = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        issue_date: new Date(formData.issue_date).toISOString(),
        expiry_date: formData.expiry_date ? new Date(formData.expiry_date).toISOString() : null
      };

      await axios.put(`${API}/safety-tickets/${selectedTicket.id}`, submitData);
      toast.success('Safety ticket updated successfully');
      setIsEditOpen(false);
      resetForm();
      fetchTickets();
    } catch (error) {
      console.error('Error updating safety ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to update safety ticket');
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (window.confirm('Are you sure you want to delete this safety ticket?')) {
      try {
        await axios.delete(`${API}/safety-tickets/${ticketId}`);
        toast.success('Safety ticket deleted successfully');
        fetchTickets();
      } catch (error) {
        console.error('Error deleting safety ticket:', error);
        toast.error('Failed to delete safety ticket');
      }
    }
  };

  // ---- Subcontractor certificates ----
  const fetchSubCerts = async () => {
    try {
      const r = await axios.get(`${API}/subcontractor-certs`);
      setSubCerts(Array.isArray(r.data) ? r.data : []);
    } catch (e) { /* ignore */ }
  };

  const resetSubForm = () => setSubForm({
    company_name: '', worker_name: '', certificate_type: '',
    issue_date: '', expiry_date: '', work_order_number: '', notes: ''
  });

  const handleCreateSub = async (e) => {
    e.preventDefault();
    if (!subForm.company_name.trim() || !subForm.worker_name.trim() || !subForm.certificate_type.trim()) {
      toast.error('Company, worker and certificate type are required');
      return;
    }
    setSubUploading(true);
    try {
      const fd = new FormData();
      fd.append('company_name', subForm.company_name);
      fd.append('worker_name', subForm.worker_name);
      fd.append('certificate_type', subForm.certificate_type);
      if (subForm.issue_date) fd.append('issue_date', subForm.issue_date);
      if (subForm.expiry_date) fd.append('expiry_date', subForm.expiry_date);
      if (subForm.work_order_number) fd.append('work_order_number', subForm.work_order_number);
      if (subForm.notes) fd.append('notes', subForm.notes);
      if (subFile) fd.append('certificate_file', subFile);
      const r = await axios.post(`${API}/subcontractor-certs`, fd);
      if (subFile && !r.data.drive_uploaded) {
        toast.warning('Cert saved, but the file could not be filed to Drive. Check the Work Order number.');
      } else {
        toast.success('Subcontractor cert saved');
      }
      setIsSubOpen(false);
      resetSubForm();
      setSubFile(null);
      fetchSubCerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save subcontractor cert');
    } finally {
      setSubUploading(false);
    }
  };

  const startEditSub = (c) => {
    setSelectedSub(c);
    setSubForm({
      company_name: c.company_name || '',
      worker_name: c.worker_name || '',
      certificate_type: c.certificate_type || '',
      issue_date: c.issue_date ? c.issue_date.slice(0, 10) : '',
      expiry_date: c.expiry_date ? c.expiry_date.slice(0, 10) : '',
      work_order_number: c.work_order_number || '',
      notes: c.notes || ''
    });
    setIsSubEditOpen(true);
  };

  const handleUpdateSub = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/subcontractor-certs/${selectedSub.id}`, {
        company_name: subForm.company_name,
        worker_name: subForm.worker_name,
        certificate_type: subForm.certificate_type,
        issue_date: subForm.issue_date || null,
        expiry_date: subForm.expiry_date || null,
        work_order_number: subForm.work_order_number || null,
        notes: subForm.notes || null
      });
      toast.success('Subcontractor cert updated');
      setIsSubEditOpen(false);
      resetSubForm();
      fetchSubCerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDeleteSub = async (id) => {
    if (!window.confirm('Delete this subcontractor cert?')) return;
    try {
      await axios.delete(`${API}/subcontractor-certs/${id}`);
      toast.success('Deleted');
      fetchSubCerts();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const viewSubFile = async (id) => {
    try {
      const r = await axios.get(`${API}/subcontractor-certs/${id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('No file available or could not open');
    }
  };

  const filteredSubCerts = subCerts.filter(c => {
    const s = searchTerm.toLowerCase();
    const matchesSearch = (c.company_name || '').toLowerCase().includes(s) ||
      (c.worker_name || '').toLowerCase().includes(s) ||
      (c.certificate_type || '').toLowerCase().includes(s);
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleBatchImport = async () => {
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }

    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/safety-tickets/batch-import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const result = response.data;
      
      if (result.success_count > 0) {
        toast.success(`Successfully imported ${result.success_count} safety tickets`);
      }
      
      if (result.error_count > 0) {
        toast.error(`${result.error_count} errors occurred during import`);
        console.log('Import errors:', result.errors);
      }

      setIsImportOpen(false);
      setImportFile(null);
      fetchTickets();
      
    } catch (error) {
      console.error('Error importing safety tickets:', error);
      toast.error(error.response?.data?.detail || 'Failed to import safety tickets');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/safety-tickets/template/csv`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'safety_tickets_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const resetForm = () => {
    setFormData({
      employee_name: '',
      employee_email: '',
      certificate_type: '',
      course_name: '',
      issuing_organization: '',
      certificate_number: '',
      issue_date: '',
      expiry_date: '',
      equipment_type: '',
      classes_covered: '',
      trainer_name: '',
      reference_number: '',
      description: '',
      project_id: ''
    });
    setSelectedTicket(null);
  };

  const startEdit = (ticket) => {
    setSelectedTicket(ticket);
    setFormData({
      employee_name: ticket.employee_name || '',
      employee_email: ticket.employee_email || '',
      certificate_type: ticket.certificate_type || '',
      course_name: ticket.course_name || '',
      issuing_organization: ticket.issuing_organization || '',
      certificate_number: ticket.certificate_number || '',
      issue_date: ticket.issue_date ? new Date(ticket.issue_date).toISOString().split('T')[0] : '',
      expiry_date: ticket.expiry_date ? new Date(ticket.expiry_date).toISOString().split('T')[0] : '',
      equipment_type: ticket.equipment_type || '',
      classes_covered: ticket.classes_covered || '',
      trainer_name: ticket.trainer_name || '',
      reference_number: ticket.reference_number || '',
      description: ticket.description || '',
      project_id: ticket.project_id || ''
    });
    setIsEditOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      superseded: 'bg-slate-100 text-slate-500',
      pending_renewal: 'bg-amber-100 text-amber-800',
      cancelled: 'bg-white text-slate-700'
    };
    return `px-3 py-1 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-white text-slate-700'}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-700" />;
      case 'expired':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'pending_renewal':
        return <Clock className="h-4 w-4 text-amber-700" />;
      default:
        return <Shield className="h-4 w-4 text-slate-500" />;
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.certificate_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.issuing_organization.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (!hasPermission && !canManageSubs && !canSeeEmployees) {
    return (
      <div className="animate-fade-in">
        <Card className="card">
          <CardContent className="p-12 text-center">
            <Shield className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Access Restricted</h3>
            <p className="text-slate-500">You don't have permission to view safety tickets. Contact your administrator for access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && !(canSeeEmployees && activeTab === 'employees')) {
    return (
      <div className="animate-fade-in">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Employee Certifications</h1>
            <p className="text-slate-500">Certificates, safety tickets & training records</p>
          </div>
          <div className="skeleton h-10 w-32"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card">
              <div className="card-body">
                <div className="skeleton h-4 w-full mb-2"></div>
                <div className="skeleton h-4 w-2/3 mb-4"></div>
                <div className="skeleton h-8 w-20"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Employee Certifications</h1>
          <p className="text-slate-500">Certificates, safety tickets & training records</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white p-1 rounded-xl border border-slate-200 mb-6 overflow-x-auto">
        {[
          ...(canSeeEmployees ? [{ id: 'employees', label: 'Employees', icon: Users }] : []),
          ...(hasPermission ? [
            { id: 'tickets', label: 'All Tickets', icon: Shield },
            { id: 'cardscans', label: 'Card Scans', icon: ScanLine },
            { id: 'training', label: 'Mass Training', icon: AlertTriangle },
            { id: 'bundle', label: 'Bundle & Send', icon: FileText },
          ] : []),
          ...(canManageSubs ? [{ id: 'subcontractors', label: 'Subcontractors', icon: HardHat }] : []),
        ].map(t => (
          <button key={t.id} data-testid={`safety-tab-${t.id}`} onClick={() => {
            setActiveTab(t.id);
            if (t.id === 'cardscans') {
              fetchCardReviews();
              if (allEmployees.length === 0) loadEmployees();
            }
            if (t.id === 'subcontractors') {
              fetchSubCerts();
            }
          }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === t.id ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
            {t.id === 'cardscans' && cardPending > 0 && (
              <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-amber-500 text-white">{cardPending}</span>
            )}
          </button>
        ))}
      </div>

      {/* Employees (certificates compliance overview) Tab */}
      {activeTab === 'employees' && canSeeEmployees && (
        <div data-testid="employees-tab-content">
          <HRDashboardCompact embedded canUpload={hasPermission} onUploadCertificate={hasPermission ? () => setActiveTab('tickets') : undefined} />
        </div>
      )}

      {/* Mass Training Tab */}
      {activeTab === 'training' && <MassTrainingPanel />}

      {/* Bundle & Send Tab */}
      {activeTab === 'bundle' && (
        <div className="space-y-4 mb-8 max-w-3xl">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-slate-900 font-semibold mb-3">Select Employees for Certificate Bundle</h3>
            <p className="text-xs text-slate-500 mb-3">Select employees to generate a PDF of all their valid safety certificates and email to a client.</p>
            <div className="space-y-1 max-h-[250px] overflow-y-auto mb-3">
              {allEmployees.filter(e => e.full_name).sort((a,b) => (a.full_name||'').localeCompare(b.full_name||'')).map(emp => (
                <label key={emp.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 cursor-pointer">
                  <input type="checkbox" checked={bundleEmployees.includes(emp.id)}
                    onChange={() => setBundleEmployees(prev => prev.includes(emp.id) ? prev.filter(x => x !== emp.id) : [...prev, emp.id])}
                    className="rounded border-slate-500" />
                  <span className="text-sm text-slate-900">{emp.full_name}</span>
                  <span className="text-xs text-slate-500">{emp.role}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="text-xs text-slate-500">Client Email (optional — leave blank to preview)</label>
                <input value={bundleEmail} onChange={e => setBundleEmail(e.target.value)} placeholder="client@company.com"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none" />
              </div>
              <button onClick={async () => {
                if (bundleEmployees.length === 0) { toast.error('Select at least one employee'); return; }
                setBundleSending(true);
                try {
                  const r = await axios.post(`${API}/certificates/bundle-pdf`, {
                    employee_ids: bundleEmployees,
                    recipient_email: bundleEmail || undefined,
                  });
                  if (r.data.status === 'emailed') {
                    toast.success(`Emailed ${r.data.cert_count} certificates to ${r.data.to}`);
                  } else {
                    // Preview — open in new window
                    const pw = window.open('', '_blank');
                    if (pw) { pw.document.write(r.data.html); pw.document.close(); }
                    toast.success(`Generated bundle: ${r.data.cert_count} certificates for ${r.data.employee_count} employees`);
                  }
                } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                setBundleSending(false);
              }} disabled={bundleSending}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white rounded-lg text-sm font-medium whitespace-nowrap">
                {bundleSending ? 'Generating...' : bundleEmail ? 'Generate & Email PDF' : 'Preview PDF'}
              </button>
            </div>
            {bundleEmployees.length > 0 && (
              <p className="text-xs text-blue-700 mt-2">{bundleEmployees.length} employee(s) selected</p>
            )}
          </div>
        </div>
      )}

      {/* Card Scans Tab */}
      {activeTab === 'cardscans' && (
        <div className="space-y-6 mb-8" data-testid="card-scans-tab">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-slate-700 text-sm">Training cards emailed to the scan inbox appear here. Verify each card, then file them to Google Drive &amp; safety records in one click.</p>
              <p className="text-xs text-slate-500 mt-1">Email a scanned page (~6 cards) with subject containing <span className="text-slate-700 font-medium">"SAFETY CARDS"</span>.</p>
            </div>
            <Button data-testid="card-scan-poll-btn" onClick={handleCardPollNow} disabled={cardPolling} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Mail className={`h-4 w-4 mr-2 ${cardPolling ? 'animate-pulse' : ''}`} />
              {cardPolling ? 'Checking…' : 'Check Inbox Now'}
            </Button>
          </div>

          {cardLoading ? (
            <p className="text-slate-500 text-center py-8">Loading…</p>
          ) : cardReviews.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center" data-testid="card-scans-empty">
              <ScanLine className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500">No scans waiting for review.</p>
            </div>
          ) : cardReviews.map(rev => (
            <div key={rev.id} data-testid={`card-review-${rev.id}`} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <p className="text-slate-900 font-semibold text-sm">{rev.subject || 'Scanned page'}</p>
                  <p className="text-xs text-slate-500">From {rev.sender || 'unknown'} · {rev.card_count} card(s) · {new Date(rev.received_at).toLocaleString()}</p>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`card-review-reject-${rev.id}`} variant="outline" onClick={() => handleRejectReview(rev.id)} className="text-red-700 border-red-200 hover:bg-red-50">
                    <X className="h-4 w-4 mr-1" /> Discard
                  </Button>
                  <Button data-testid={`card-review-confirm-${rev.id}`} onClick={() => handleConfirmReview(rev)} disabled={confirmingId === rev.id} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Check className="h-4 w-4 mr-1" /> {confirmingId === rev.id ? 'Filing…' : 'Confirm & File All'}
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {(rev.cards || []).map(c => {
                  const e = cardEdits[c.card_id] || {};
                  return (
                    <div key={c.card_id} data-testid={`card-${c.card_id}`} className={`rounded-lg border p-3 ${e.include ? 'border-slate-300 bg-slate-50' : 'border-slate-200 bg-slate-50 opacity-60'}`}>
                      {c.has_context ? (
                        <div className="mb-2">
                          <CardCropAdjuster
                            testId={`card-crop-${c.card_id}`}
                            imageUrl={`${API}/safety-cards/reviews/${rev.id}/card/${c.card_id}/context?token=${localStorage.getItem('token')}`}
                            rect={e.crop_rect || c.crop_rect}
                            onChange={(rect) => updateCardEdit(c.card_id, 'crop_rect', rect)}
                          />
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] text-slate-500">Drag the green box / corners to fix the crop</span>
                            <button
                              type="button"
                              data-testid={`card-crop-reset-${c.card_id}`}
                              onClick={() => updateCardEdit(c.card_id, 'crop_rect', c.crop_rect)}
                              className="text-[10px] text-blue-700 underline"
                            >
                              Reset
                            </button>
                          </div>
                        </div>
                      ) : (
                        <img src={`${API}/safety-cards/reviews/${rev.id}/card/${c.card_id}/image?token=${localStorage.getItem('token')}`} alt="safety card" className="w-full h-32 object-contain bg-white rounded mb-2" />
                      )}
                      <label className="flex items-center gap-2 mb-2 text-xs text-slate-700">
                        <input type="checkbox" checked={!!e.include} disabled={cardResults[c.card_id]?.status === 'filed'} onChange={() => updateCardEdit(c.card_id, 'include', !e.include)} className="rounded" data-testid={`card-include-${c.card_id}`} />
                        Include this card
                        {c.confidence === 'low' && <span className="text-amber-700">· low confidence</span>}
                        {cardResults[c.card_id]?.status === 'filed' && <span className="text-emerald-700" data-testid={`card-filed-${c.card_id}`}>· filed ✓</span>}
                        {cardResults[c.card_id]?.status === 'error' && <span className="text-red-600" title={cardResults[c.card_id]?.detail} data-testid={`card-failed-${c.card_id}`}>· failed: {cardResults[c.card_id]?.detail || 'could not file'}</span>}
                      </label>
                      <div className="space-y-2">
                        <div>
                          <label className="text-[11px] text-slate-500">Employee {!e.employee_id && <span className="text-amber-700">(no match — pick)</span>}
                            {allEmployees.length === 0 && (
                              <button type="button" onClick={loadEmployees} className="ml-2 text-blue-700 underline" data-testid="card-emp-reload">reload list</button>
                            )}
                          </label>
                          <select value={e.employee_id || ''} onChange={ev => selectCardEmployee(c.card_id, ev.target.value)}
                            data-testid={`card-emp-${c.card_id}`}
                            className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-slate-900 text-sm outline-none">
                            <option value="">{e.employee_name ? `${e.employee_name} (detected)` : '— Select employee —'}</option>
                            {allEmployees.filter(x => x.full_name).sort((a,b)=>(a.full_name||'').localeCompare(b.full_name||'')).map(x => (
                              <option key={x.id} value={x.id}>{x.full_name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[11px] text-slate-500">Ticket / Cert</label>
                          <input value={e.ticket_name || ''} onChange={ev => updateCardEdit(c.card_id, 'ticket_name', ev.target.value)}
                            data-testid={`card-ticket-${c.card_id}`}
                            className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-slate-900 text-sm outline-none" />
                        </div>
                        <div>
                          <label className="text-[11px] text-slate-500">Expiry</label>
                          <input type="date" value={e.expiry_date ? e.expiry_date.slice(0,10) : ''} onChange={ev => updateCardEdit(c.card_id, 'expiry_date', ev.target.value)}
                            data-testid={`card-expiry-${c.card_id}`}
                            className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-slate-900 text-sm outline-none" />
                          {c.expiry_note && <p className="text-[10px] text-amber-700 mt-0.5" data-testid={`card-expiry-note-${c.card_id}`}>{c.expiry_note}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Archive replaced/expired tickets modal */}
      <Dialog open={!!archiveModal} onOpenChange={(o) => { if (!o && !archiving) { setArchiveModal(null); setArchiveChecks({}); } }}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="archive-expired-modal">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-700" /> Move replaced tickets to Expired?
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              The newly filed cards look like updated versions of these older files already in each employee's
              Tickets &amp; Certificates folder. Uncheck any you want to keep in place. Checked files will be moved
              to that employee's <span className="text-slate-800 font-medium">Expired Tickets</span> folder.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {(archiveModal?.candidates || []).map((cd, ci) => (
              <div key={cd.card_id || ci} className="rounded-lg border border-slate-300 bg-slate-50 p-3" data-testid={`archive-group-${ci}`}>
                <p className="text-sm text-slate-900 font-medium">{cd.employee_name}</p>
                <p className="text-xs text-slate-500 mb-2">
                  New: <span className="text-emerald-700">{cd.new_ticket_name}</span> — older copies found:
                </p>
                <div className="space-y-1.5">
                  {(cd.matches || []).map(m => (
                    <label key={m.file_id} className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer" data-testid={`archive-match-${m.file_id}`}>
                      <input
                        type="checkbox"
                        checked={!!archiveChecks[m.file_id]}
                        onChange={() => setArchiveChecks(prev => ({ ...prev, [m.file_id]: !prev[m.file_id] }))}
                        className="rounded mt-0.5"
                        data-testid={`archive-check-${m.file_id}`}
                      />
                      <span className="break-all">{m.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" data-testid="archive-skip-btn" disabled={archiving}
              onClick={() => { setArchiveModal(null); setArchiveChecks({}); }}
              className="text-slate-700 border-slate-500/40 hover:bg-slate-100">
              Keep all / Skip
            </Button>
            <Button data-testid="archive-confirm-btn" disabled={archiving}
              onClick={handleArchiveExpired}
              className="bg-amber-600 hover:bg-amber-700 text-white">
              {archiving ? 'Moving…' : 'Move checked to Expired'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tickets Tab */}
      {activeTab === 'tickets' && <>
      {hasPermission && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4" data-testid="tickets-pdf-generator">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-4 w-4 text-blue-700" />
            <h3 className="text-slate-900 font-semibold text-sm">Generate Safety Tickets PDF</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Builds a letterhead PDF of an employee's current (non-expired) tickets from their Google Drive
            "Tickets &amp; Certificates" folder and saves it into their employee folder.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <Select value={pdfEmployeeId} onValueChange={setPdfEmployeeId}>
              <SelectTrigger className="w-full sm:w-80 bg-slate-100 border-slate-300 text-slate-900" data-testid="tickets-pdf-employee-select">
                <SelectValue placeholder="Select employee…" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900 max-h-72">
                {allEmployees.map(e => (
                  <SelectItem key={e.id} value={e.id} className="text-slate-900">{e.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid="tickets-pdf-generate-btn"
              disabled={!pdfEmployeeId || pdfGenerating}
              onClick={handleGenerateTicketsPdf}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-70"
            >
              {pdfGenerating
                ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />{pdfProgress || 'Generating…'}</>
                : <><FileText className="h-4 w-4 mr-2" />Generate PDF</>}
            </Button>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Button
                data-testid="tickets-pdf-bulk-btn"
                disabled={bulkRunning}
                onClick={() => handleBulkGenerate(false)}
                className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-70"
              >
                {bulkRunning
                  ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {bulkStatus?.total
                        ? `Generating ${bulkStatus.processed || 0}/${bulkStatus.total}${bulkStatus.current ? ` — ${bulkStatus.current}` : ''}`
                        : 'Starting…'}</>
                  : <><FileText className="h-4 w-4 mr-2" />Generate All — Everyone's PDF</>}
              </Button>
              {bulkStatus?.status === 'interrupted' && (
                <Button
                  data-testid="tickets-pdf-bulk-resume-btn"
                  onClick={() => handleBulkGenerate(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Resume ({(bulkStatus.results || []).filter(r => r.status === 'done').length} already done)
                </Button>
              )}
              <p className="text-xs text-slate-500">
                Builds a fresh PDF for every employee folder. Each person's old PDF is moved to an
                "Expired" folder in their Drive folder first.
              </p>
            </div>
            {bulkStatus?.status === 'interrupted' && (
              <p className="mt-2 text-xs text-amber-700" data-testid="bulk-interrupted-msg">
                ⚠ {bulkStatus.message || 'The last bulk run was interrupted — Resume finishes the remaining employees.'}
              </p>
            )}
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
              <Button
                data-testid="qr-stickers-btn"
                disabled={qrGenerating}
                onClick={handleQrStickers}
                className="bg-slate-100 hover:bg-slate-200 text-slate-900 disabled:opacity-70"
              >
                {qrGenerating
                  ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Building stickers…</>
                  : <><QrCode className="h-4 w-4 mr-2" />Hardhat QR Stickers</>}
              </Button>
              <p className="text-xs text-slate-500">
                Printable sticker sheet — scanning a hardhat QR shows that person's live tickets & certs.
                Works for anyone (customer safety reps), no login needed.
              </p>
            </div>
            {bulkStatus?.status === 'done' && (bulkStatus.results || []).length > 0 && (
              <div className="mt-2 text-xs" data-testid="bulk-results-summary">
                <span className="text-emerald-700">
                  ✓ {(bulkStatus.results || []).filter(r => r.status === 'done').length} generated
                </span>
                {(bulkStatus.results || []).some(r => r.status !== 'done') && (
                  <span className="text-amber-700">
                    {' '}· skipped: {(bulkStatus.results || []).filter(r => r.status !== 'done').map(r => `${r.employee} (${r.error || 'error'})`).join('; ')}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="flex justify-end items-center flex-wrap gap-3 mb-6">
        {hasPermission && driveScanStatus && driveScanStatus.status !== 'running' && driveScanStatus.finished_at && (
          <span className={`text-xs ${driveScanStatus.status === 'error' ? 'text-red-700' : 'text-slate-500'}`} data-testid="drive-scan-last-run"
            title={driveScanStatus.failed_employees?.length ? `Skipped folders:\n${driveScanStatus.failed_employees.join('\n')}` : ''}>
            {driveScanStatus.status === 'error'
              ? `Last Drive scan failed ${new Date(driveScanStatus.finished_at).toLocaleString()}: ${driveScanStatus.error}`
              : `Last Drive scan ${new Date(driveScanStatus.finished_at).toLocaleString()}${driveScanStatus.started_by ? ` by ${driveScanStatus.started_by.split(' ')[0]}` : ''} — ${driveScanStatus.tickets_imported || 0} new, ${driveScanStatus.tickets_updated || 0} updated${driveScanStatus.tickets_relinked ? `, ${driveScanStatus.tickets_relinked} re-linked` : ''}${(driveScanStatus.failed_count || driveScanStatus.failed_employees?.length) ? `, ${driveScanStatus.failed_count || driveScanStatus.failed_employees.length} folder(s) skipped` : ''}`}
          </span>
        )}
        {hasPermission && (
          <Button
            data-testid="scan-drive-now-btn"
            onClick={handleRescanDrive}
            disabled={driveScanning}
            className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-70"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${driveScanning ? 'animate-spin' : ''}`} />
            {driveScanning
              ? (driveScanStatus?.total_employees
                  ? `Scanning ${driveScanStatus.processed_employees || 0}/${driveScanStatus.total_employees}…`
                  : 'Scanning…')
              : 'Scan Drive Now'}
          </Button>
        )}
        {user?.role === 'super_admin' && (
          <Button
            data-testid="standardize-filenames-btn"
            onClick={() => setShowRename(true)}
            variant="outline"
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            title="One-time clean-up: rename every ticket in Drive to 'Course - First Last - Exp YYYY-MM-DD'"
          >
            <FolderPen className="h-4 w-4 mr-2" />
            Standardize Drive filenames
          </Button>
        )}
        {showRename && <TicketRenameTool onClose={() => setShowRename(false)} />}
        <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              <Upload className="h-4 w-4 mr-2" />
              Import CSV
            </Button>
          </DialogTrigger>
            <DialogContent className="bg-white border-slate-200">
              <DialogHeader>
                <DialogTitle className="text-slate-900">Batch Import Safety Tickets</DialogTitle>
                <DialogDescription className="text-slate-500">
                  Upload a CSV file to import multiple safety tickets at once
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-800">CSV File</Label>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="bg-slate-100 border-slate-300 text-slate-900"
                  />
                </div>
                <div className="flex justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={downloadTemplate}
                    className="text-slate-800"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </Button>
                  <div className="flex space-x-2">
                    <Button type="button" variant="outline" onClick={() => setIsImportOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleBatchImport}
                      disabled={!importFile || importing}
                      className="btn-primary"
                    >
                      {importing ? 'Importing...' : 'Import'}
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Create New Ticket Button */}
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                New Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-slate-900">Create New Safety Ticket</DialogTitle>
                <DialogDescription className="text-slate-500">
                  Add a new safety certification or training record
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateTicket} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="employee_name" className="text-slate-800">Employee Name *</Label>
                    <Input
                      id="employee_name"
                      value={formData.employee_name}
                      onChange={(e) => setFormData({...formData, employee_name: e.target.value})}
                      className="bg-slate-100 border-slate-300 text-slate-900"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="employee_email" className="text-slate-800">Employee Email</Label>
                    <Input
                      id="employee_email"
                      type="email"
                      value={formData.employee_email}
                      onChange={(e) => setFormData({...formData, employee_email: e.target.value})}
                      className="bg-slate-100 border-slate-300 text-slate-900"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="certificate_type" className="text-slate-800">Certificate Type *</Label>
                    <Input
                      id="certificate_type"
                      value={formData.certificate_type}
                      onChange={(e) => setFormData({...formData, certificate_type: e.target.value})}
                      placeholder="e.g., WHMIS 2015/GHS"
                      className="bg-slate-100 border-slate-300 text-slate-900"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="issuing_organization" className="text-slate-800">Issuing Organization *</Label>
                    <Input
                      id="issuing_organization"
                      value={formData.issuing_organization}
                      onChange={(e) => setFormData({...formData, issuing_organization: e.target.value})}
                      placeholder="e.g., C&M Safety"
                      className="bg-slate-100 border-slate-300 text-slate-900"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="course_name" className="text-slate-800">Course Name</Label>
                  <Input
                    id="course_name"
                    value={formData.course_name}
                    onChange={(e) => setFormData({...formData, course_name: e.target.value})}
                    className="bg-slate-100 border-slate-300 text-slate-900"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="issue_date" className="text-slate-800">Issue Date *</Label>
                    <Input
                      id="issue_date"
                      type="date"
                      value={formData.issue_date}
                      onChange={(e) => setFormData({...formData, issue_date: e.target.value})}
                      className="bg-slate-100 border-slate-300 text-slate-900"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="expiry_date" className="text-slate-800">Expiry Date</Label>
                    <Input
                      id="expiry_date"
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                      className="bg-slate-100 border-slate-300 text-slate-900"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="certificate_number" className="text-slate-800">Certificate Number</Label>
                    <Input
                      id="certificate_number"
                      value={formData.certificate_number}
                      onChange={(e) => setFormData({...formData, certificate_number: e.target.value})}
                      className="bg-slate-100 border-slate-300 text-slate-900"
                    />
                  </div>
                  <div>
                    <Label htmlFor="equipment_type" className="text-slate-800">Equipment Type</Label>
                    <Input
                      id="equipment_type"
                      value={formData.equipment_type}
                      onChange={(e) => setFormData({...formData, equipment_type: e.target.value})}
                      placeholder="e.g., Scissor Lift, Crane"
                      className="bg-slate-100 border-slate-300 text-slate-900"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="classes_covered" className="text-slate-800">Classes Covered</Label>
                    <Input
                      id="classes_covered"
                      value={formData.classes_covered}
                      onChange={(e) => setFormData({...formData, classes_covered: e.target.value})}
                      placeholder="e.g., 1,4,5,7 Lift truck"
                      className="bg-slate-100 border-slate-300 text-slate-900"
                    />
                  </div>
                  <div>
                    <Label htmlFor="trainer_name" className="text-slate-800">Trainer Name</Label>
                    <Input
                      id="trainer_name"
                      value={formData.trainer_name}
                      onChange={(e) => setFormData({...formData, trainer_name: e.target.value})}
                      className="bg-slate-100 border-slate-300 text-slate-900"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description" className="text-slate-800">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="bg-slate-100 border-slate-300 text-slate-900"
                    rows={3}
                  />
                </div>

                <div className="flex justify-end space-x-4">
                  <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary">
                    Create Ticket
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

      {/* Filters */}
      <div className="flex space-x-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            placeholder="Search tickets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-slate-300 text-slate-900"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 bg-white border-slate-300 text-slate-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending_renewal">Pending Renewal</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tickets Grid */}
      {filteredTickets.length === 0 ? (
        <Card className="card">
          <CardContent className="p-12 text-center">
            <Shield className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No safety tickets found</h3>
            <p className="text-slate-500 mb-6">
              {tickets.length === 0 ? 'Create your first safety ticket or import from CSV' : 'Try adjusting your search or filters'}
            </p>
            {tickets.length === 0 && (
              <div className="flex justify-center space-x-4">
                <Button onClick={() => setIsCreateOpen(true)} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Ticket
                </Button>
                <Button onClick={() => setIsImportOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Upload className="h-4 w-4 mr-2" />
                  Import from CSV
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTickets.map((ticket) => (
            <Card key={ticket.id} className="card hover-lift">
              <CardHeader className="card-header">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-slate-900 text-lg flex items-center">
                      {getStatusIcon(ticket.status)}
                      <span className="ml-2">{ticket.certificate_type}</span>
                    </CardTitle>
                    <p className="text-slate-500 text-sm">{ticket.employee_name}</p>
                  </div>
                  <span className={getStatusBadgeClass(ticket.status)}>
                    {ticket.status.replace('_', ' ')}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="card-body">
                <div className="space-y-3">
                  <div>
                    <p className="text-slate-500 text-sm">Organization</p>
                    <p className="text-slate-900 font-medium">{ticket.issuing_organization}</p>
                  </div>
                  
                  <div>
                    <p className="text-slate-500 text-sm">Issue Date</p>
                    <p className="text-slate-900">{formatDate(ticket.issue_date)}</p>
                  </div>

                  {ticket.expiry_date && (
                    <div>
                      <p className="text-slate-500 text-sm">Expiry Date</p>
                      <p className="text-slate-900">{formatDate(ticket.expiry_date)}</p>
                    </div>
                  )}

                  {ticket.equipment_type && (
                    <div>
                      <p className="text-slate-500 text-sm">Equipment</p>
                      <p className="text-slate-900">{ticket.equipment_type}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end items-center pt-4 border-t border-slate-200 space-x-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEdit(ticket)}
                      className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteTicket(ticket.id)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Edit Safety Ticket</DialogTitle>
            <DialogDescription className="text-slate-500">
              Update safety certification information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditTicket} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_employee_name" className="text-slate-800">Employee Name *</Label>
                <Input
                  id="edit_employee_name"
                  value={formData.employee_name}
                  onChange={(e) => setFormData({...formData, employee_name: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_employee_email" className="text-slate-800">Employee Email</Label>
                <Input
                  id="edit_employee_email"
                  type="email"
                  value={formData.employee_email}
                  onChange={(e) => setFormData({...formData, employee_email: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_certificate_type" className="text-slate-800">Certificate Type *</Label>
                <Input
                  id="edit_certificate_type"
                  value={formData.certificate_type}
                  onChange={(e) => setFormData({...formData, certificate_type: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_issuing_organization" className="text-slate-800">Issuing Organization *</Label>
                <Input
                  id="edit_issuing_organization"
                  value={formData.issuing_organization}
                  onChange={(e) => setFormData({...formData, issuing_organization: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit_course_name" className="text-slate-800">Course Name</Label>
              <Input
                id="edit_course_name"
                value={formData.course_name}
                onChange={(e) => setFormData({...formData, course_name: e.target.value})}
                className="bg-slate-100 border-slate-300 text-slate-900"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_issue_date" className="text-slate-800">Issue Date *</Label>
                <Input
                  id="edit_issue_date"
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => setFormData({...formData, issue_date: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_expiry_date" className="text-slate-800">Expiry Date</Label>
                <Input
                  id="edit_expiry_date"
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_certificate_number" className="text-slate-800">Certificate Number</Label>
                <Input
                  id="edit_certificate_number"
                  value={formData.certificate_number}
                  onChange={(e) => setFormData({...formData, certificate_number: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                />
              </div>
              <div>
                <Label htmlFor="edit_equipment_type" className="text-slate-800">Equipment Type</Label>
                <Input
                  id="edit_equipment_type"
                  value={formData.equipment_type}
                  onChange={(e) => setFormData({...formData, equipment_type: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_classes_covered" className="text-slate-800">Classes Covered</Label>
                <Input
                  id="edit_classes_covered"
                  value={formData.classes_covered}
                  onChange={(e) => setFormData({...formData, classes_covered: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                />
              </div>
              <div>
                <Label htmlFor="edit_trainer_name" className="text-slate-800">Trainer Name</Label>
                <Input
                  id="edit_trainer_name"
                  value={formData.trainer_name}
                  onChange={(e) => setFormData({...formData, trainer_name: e.target.value})}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit_description" className="text-slate-800">Description</Label>
              <Textarea
                id="edit_description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-slate-100 border-slate-300 text-slate-900"
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-4">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Update Ticket
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      </>}

      {/* Subcontractors Tab */}
      {activeTab === 'subcontractors' && (
        <div data-testid="subcontractors-tab">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Subcontractor Certificates</h2>
              <p className="text-slate-500 text-sm">Safety tickets &amp; certs for non-employee subcontractors. Files are saved to the project's "Subcontractor Certs" Drive folder.</p>
            </div>
            <Button onClick={() => { resetSubForm(); setSubFile(null); setIsSubOpen(true); }} className="btn-primary" data-testid="add-sub-cert-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Subcontractor Cert
            </Button>
          </div>

          {/* Filters */}
          <div className="flex space-x-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                placeholder="Search company, worker, or cert..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-slate-300 text-slate-900"
                data-testid="sub-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-white border-slate-300 text-slate-900" data-testid="sub-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending_renewal">Pending Renewal</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Grid */}
          {filteredSubCerts.length === 0 ? (
            <Card className="card">
              <CardContent className="p-12 text-center">
                <HardHat className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No subcontractor certs yet</h3>
                <p className="text-slate-500 mb-6">Add a subcontractor's safety ticket or certificate to start tracking expiry.</p>
                <Button onClick={() => { resetSubForm(); setSubFile(null); setIsSubOpen(true); }} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" /> Add Subcontractor Cert
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSubCerts.map((c) => (
                <Card key={c.id} className="card hover-lift" data-testid={`sub-cert-${c.id}`}>
                  <CardHeader className="card-header">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-slate-900 text-lg flex items-center">
                          {getStatusIcon(c.status)}
                          <span className="ml-2">{c.certificate_type}</span>
                        </CardTitle>
                        <p className="text-slate-700 text-sm">{c.worker_name}</p>
                        <p className="text-slate-500 text-xs">{c.company_name}</p>
                      </div>
                      <span className={getStatusBadgeClass(c.status)}>{c.status.replace('_', ' ')}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="card-body">
                    <div className="space-y-3">
                      {c.work_order_number && (<div><p className="text-slate-500 text-sm">Work Order</p><p className="text-slate-900">{c.work_order_number}</p></div>)}
                      {c.issue_date && (<div><p className="text-slate-500 text-sm">Issue Date</p><p className="text-slate-900">{formatDate(c.issue_date)}</p></div>)}
                      {c.expiry_date && (<div><p className="text-slate-500 text-sm">Expiry Date</p><p className="text-slate-900">{formatDate(c.expiry_date)}</p></div>)}
                      {c.notes && (<div><p className="text-slate-500 text-sm">Notes</p><p className="text-slate-900 text-sm whitespace-pre-wrap">{c.notes}</p></div>)}
                      <div className="flex justify-end items-center pt-4 border-t border-slate-200 space-x-2">
                        {c.google_drive_file_id && (
                          <Button size="sm" variant="ghost" onClick={() => viewSubFile(c.id)} className="h-8 px-2 text-blue-700 hover:text-blue-700 hover:bg-slate-100" data-testid={`sub-view-${c.id}`}>
                            <Eye className="h-4 w-4 mr-1" /> File
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => startEditSub(c)} className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900 hover:bg-slate-100" data-testid={`sub-edit-${c.id}`}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteSub(c.id)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`sub-delete-${c.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Add Subcontractor Cert Dialog */}
          <Dialog open={isSubOpen} onOpenChange={setIsSubOpen}>
            <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-slate-900">Add Subcontractor Certificate</DialogTitle>
                <DialogDescription className="text-slate-500">Record a subcontractor's safety ticket/cert. Attach a photo or PDF to file it into the project folder.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateSub} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label className="text-slate-800">Company Name *</Label>
                    <Input value={subForm.company_name} onChange={(e) => setSubForm({ ...subForm, company_name: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="sub-company-input" required /></div>
                  <div><Label className="text-slate-800">Worker Name *</Label>
                    <Input value={subForm.worker_name} onChange={(e) => setSubForm({ ...subForm, worker_name: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="sub-worker-input" required /></div>
                  <div><Label className="text-slate-800">Certificate / Ticket Type *</Label>
                    <Input value={subForm.certificate_type} onChange={(e) => setSubForm({ ...subForm, certificate_type: e.target.value })} placeholder="e.g., Working at Heights" className="bg-white border-slate-300 text-slate-900" data-testid="sub-type-input" required /></div>
                  <div><Label className="text-slate-800">Work Order (optional)</Label>
                    <Input value={subForm.work_order_number} onChange={(e) => setSubForm({ ...subForm, work_order_number: e.target.value })} placeholder="e.g., 26-3000" className="bg-white border-slate-300 text-slate-900" data-testid="sub-wo-input" /></div>
                  <div><Label className="text-slate-800">Issue Date</Label>
                    <Input type="date" value={subForm.issue_date} onChange={(e) => setSubForm({ ...subForm, issue_date: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="sub-issue-input" /></div>
                  <div><Label className="text-slate-800">Expiry Date</Label>
                    <Input type="date" value={subForm.expiry_date} onChange={(e) => setSubForm({ ...subForm, expiry_date: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="sub-expiry-input" /></div>
                </div>
                <div><Label className="text-slate-800">Notes</Label>
                  <Textarea value={subForm.notes} onChange={(e) => setSubForm({ ...subForm, notes: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="sub-notes-input" /></div>
                <div><Label className="text-slate-800">Certificate File (photo or PDF)</Label>
                  <Input type="file" accept="image/*,application/pdf" onChange={(e) => setSubFile(e.target.files?.[0] || null)} className="bg-white border-slate-300 text-slate-900" data-testid="sub-file-input" />
                  {subForm.work_order_number
                    ? <p className="text-xs text-slate-500 mt-1">Will be filed into project {subForm.work_order_number} → "Subcontractor Certs".</p>
                    : <p className="text-xs text-slate-500 mt-1">Add a Work Order to file it into the project folder; otherwise it goes to the shared Subcontractor Certs folder.</p>}
                </div>
                <div className="flex justify-end space-x-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsSubOpen(false)}>Cancel</Button>
                  <Button type="submit" className="btn-primary" disabled={subUploading} data-testid="sub-submit-btn">{subUploading ? 'Saving…' : 'Save Cert'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Subcontractor Cert Dialog */}
          <Dialog open={isSubEditOpen} onOpenChange={setIsSubEditOpen}>
            <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-slate-900">Edit Subcontractor Certificate</DialogTitle>
                <DialogDescription className="text-slate-500">Update the details for this subcontractor cert.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpdateSub} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label className="text-slate-800">Company Name *</Label>
                    <Input value={subForm.company_name} onChange={(e) => setSubForm({ ...subForm, company_name: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="sub-edit-company-input" required /></div>
                  <div><Label className="text-slate-800">Worker Name *</Label>
                    <Input value={subForm.worker_name} onChange={(e) => setSubForm({ ...subForm, worker_name: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="sub-edit-worker-input" required /></div>
                  <div><Label className="text-slate-800">Certificate / Ticket Type *</Label>
                    <Input value={subForm.certificate_type} onChange={(e) => setSubForm({ ...subForm, certificate_type: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="sub-edit-type-input" required /></div>
                  <div><Label className="text-slate-800">Work Order (optional)</Label>
                    <Input value={subForm.work_order_number} onChange={(e) => setSubForm({ ...subForm, work_order_number: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="sub-edit-wo-input" /></div>
                  <div><Label className="text-slate-800">Issue Date</Label>
                    <Input type="date" value={subForm.issue_date} onChange={(e) => setSubForm({ ...subForm, issue_date: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="sub-edit-issue-input" /></div>
                  <div><Label className="text-slate-800">Expiry Date</Label>
                    <Input type="date" value={subForm.expiry_date} onChange={(e) => setSubForm({ ...subForm, expiry_date: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="sub-edit-expiry-input" /></div>
                </div>
                <div><Label className="text-slate-800">Notes</Label>
                  <Textarea value={subForm.notes} onChange={(e) => setSubForm({ ...subForm, notes: e.target.value })} className="bg-white border-slate-300 text-slate-900" data-testid="sub-edit-notes-input" /></div>
                <div className="flex justify-end space-x-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsSubEditOpen(false)}>Cancel</Button>
                  <Button type="submit" className="btn-primary" data-testid="sub-edit-submit-btn">Save Changes</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
};

export default SafetyTickets;